import React from 'react';
import type { Theme } from '../../constants/themes';

/**
 * Go-live notice for the patient-facing screens.
 *
 * Renders nothing unless the active tenant's theme supplies the copy. That gate is the point: the text
 * says "our office" and carries a real phone number, so a tenant without these keys must show nothing
 * rather than St. Luke's details.
 *
 * Mounted on all three screens a St. Luke patient can arrive at: the guarantor-verification modal and
 * the bills summary (the actual payment-link journey), and the username/password login. The first
 * version lived on the login screen ONLY, where no St. Luke patient ever goes -- it renders correctly
 * there and was invisible to every real patient.
 *
 * {@code showPhone} exists for the login screen, which already carries ContactBanner with the same
 * number; without it the phone appears twice on that one screen. The patient journey has no
 * ContactBanner, so there the notice must carry the number itself.
 */
/**
 * tel: target for a display-formatted US number.
 *
 * Digits only, plus the +1 country code when the number is a bare 10-digit US one -- matching
 * ContactBanner's `tel:+14066764441`. A bare `tel:4066764441` is less reliable on mobile.
 */
export const telHref = (display: string): string => {
  const digits = (display || '').replace(/[^0-9]/g, '');
  if (digits.length === 10) return `tel:+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`;
  return `tel:${digits}`;
};

/**
 * The labelled support number, on its own. Exported so a screen can place it where it stays on screen
 * (the guarantor modal puts it directly under Verify) and pass showPhone={false} to the notice, keeping
 * the number to once per screen. Renders nothing for a tenant without a supportPhone.
 */
export const SupportPhone: React.FC<{ phone?: string; className?: string }> = ({
  phone,
  className = 'mt-3 text-sm text-slate-600',
}) => {
  if (!phone) return null;
  return (
    <p className={className}>
      {/* Labelled, and styled like ContactBanner's number rather than as bold body text. Unlabelled
          and un-linked, this rendered as a bare bold figure under the copy with nothing saying it
          was a phone number. */}
      Phone:{' '}
      <a
        className="font-semibold text-[#71001F] underline underline-offset-2 hover:opacity-80"
        href={telHref(phone)}
        aria-label={`Call our office at ${phone}`}
      >
        {phone}
      </a>
    </p>
  );
};

const TransitionNotice: React.FC<{ theme?: Theme | null; showPhone?: boolean }> = ({
  theme,
  showPhone = true,
}) => {
  const heading = theme?.transitionNoticeHeading;
  const paragraphs = theme?.transitionNotice;
  const phone = showPhone ? theme?.supportPhone : undefined;

  // Nothing configured for this tenant, or configured empty. Render no container at all so the login
  // layout is untouched rather than gaining an empty bordered box.
  if (!heading && !(paragraphs && paragraphs.length > 0)) {
    return null;
  }

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white/70 p-4 text-left"
      role="status"
      aria-live="polite"
    >
      {heading && (
        <p className="text-sm font-semibold text-[#242E49]">{heading}</p>
      )}
      {paragraphs?.map((text) => (
        // Copy is fixed per tenant in themes.ts, so the text itself is a stable key.
        <p key={text} className="mt-2 text-xs leading-relaxed text-slate-600">
          {text}
        </p>
      ))}
      <SupportPhone phone={phone} />
    </div>
  );
};

export default TransitionNotice;
